// src/features/bom/pages/ProductionFloorDetailPage.tsx

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { ErrorState } from '@/components/ui/ErrorState';
import { TransferToFloorModal } from './components/TransferToFloorModal';
import { RecordWastageModal } from './components/RecordWastageModal';
import {
    getProductionFloor, getFloorDashboard, getFloorInventory,
    getFloorTransfers, getFloorWastage, deactivateProductionFloor
    ,
} from '../services/productionFloorApi';
import {
    ArrowLeft, Pencil, Trash2, ChefHat, Beaker, Utensils,
    Wine, Warehouse, MapPin, User, Phone, Mail, Package,
    AlertTriangle,
    Plus, RefreshCw, Truck, BarChart3,
    DollarSign, ChevronDown, ChevronUp,
    Zap, Activity, Search, X,
    ChevronLeft, ChevronRight
    ,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// CONSTANTS
// ============================================================

const FLOOR_ICONS: Record<string, React.ElementType> = {
    kitchen: ChefHat, bakery: Beaker, butchery: Utensils,
    bar: Wine, general: Warehouse,
};

const FLOOR_COLORS: Record<string, string> = {
    kitchen: 'from-amber-500 to-orange-600',
    bakery: 'from-rose-400 to-pink-600',
    butchery: 'from-red-500 to-rose-600',
    bar: 'from-purple-500 to-indigo-600',
    general: 'from-slate-500 to-slate-700',
};

const NAV_ITEMS = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'inventory', label: 'Inventory', icon: Package },
    { id: 'transfers', label: 'Transfers', icon: Truck },
    { id: 'wastage', label: 'Wastage', icon: AlertTriangle },
];

const ITEMS_PER_PAGE = 20;

// ============================================================
// ANIMATED COUNTER HOOK
// ============================================================

function useCountUp(target: number, duration: number = 1000, startCounting: boolean) {
    const [count, setCount] = useState(0);
    useEffect(() => {
        if (!startCounting || target === 0) { setCount(0); return; }
        let startTime: number;
        let animationFrame: number;
        const animate = (timestamp: number) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            setCount(Math.floor(progress * target));
            if (progress < 1) animationFrame = requestAnimationFrame(animate);
        };
        animationFrame = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(animationFrame);
    }, [target, duration, startCounting]);
    return count;
}

// ============================================================
// SKELETON LOADERS
// ============================================================

function TableSkeleton({ rows = 5 }: { rows?: number }) {
    return (
        <div className="divide-y divide-slate-100">
            {Array.from({ length: rows }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-6 py-4 animate-pulse">
                    <div className="flex-1 space-y-2">
                        <div className="h-4 bg-slate-200 rounded w-1/3" />
                        <div className="h-3 bg-slate-100 rounded w-1/4" />
                    </div>
                    <div className="w-16 h-4 bg-slate-200 rounded" />
                    <div className="w-12 h-4 bg-slate-100 rounded" />
                    <div className="w-20 h-4 bg-slate-100 rounded" />
                </div>
            ))}
        </div>
    );
}

// ============================================================
// PAGINATION COMPONENT
// ============================================================

function Pagination({ page, totalPages, totalItems, onPageChange }: {
    page: number; totalPages: number; totalItems: number; onPageChange: (p: number) => void;
}) {
    if (totalPages <= 1) return null;
    const startItem = (page - 1) * ITEMS_PER_PAGE + 1;
    const endItem = Math.min(page * ITEMS_PER_PAGE, totalItems);

    return (
        <div className="px-6 py-3 bg-slate-50/50 border-t border-slate-100 flex items-center justify-between flex-wrap gap-3">
            <p className="text-xs text-slate-500">
                Showing <span className="font-semibold text-slate-700">{startItem}-{endItem}</span> of{' '}
                <span className="font-semibold text-slate-700">{totalItems.toLocaleString()}</span>
            </p>
            <div className="flex items-center gap-1">
                <button
                    onClick={() => onPageChange(1)}
                    disabled={page === 1}
                    className="p-1.5 rounded-lg border border-slate-200 disabled:opacity-30 hover:bg-slate-100"
                    type="button"
                >
                    <ChevronLeft size={14} className="text-slate-500" />
                </button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                        pageNum = i + 1;
                    } else if (page <= 3) {
                        pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                    } else {
                        pageNum = page - 2 + i;
                    }
                    return (
                        <button
                            key={pageNum}
                            onClick={() => onPageChange(pageNum)}
                            className={clsx(
                                'w-8 h-8 rounded-lg text-xs font-semibold transition-all',
                                page === pageNum
                                    ? 'bg-amber-500 text-white shadow-sm'
                                    : 'border border-slate-200 text-slate-600 hover:bg-slate-100'
                            )}
                            type="button"
                        >
                            {pageNum}
                        </button>
                    );
                })}
                <button
                    onClick={() => onPageChange(totalPages)}
                    disabled={page === totalPages}
                    className="p-1.5 rounded-lg border border-slate-200 disabled:opacity-30 hover:bg-slate-100"
                    type="button"
                >
                    <ChevronRight size={14} className="text-slate-500" />
                </button>
            </div>
        </div>
    );
}

// ============================================================
// ANIMATED STAT CARD
// ============================================================

function AnimatedStatCard({ label, value, icon: Icon, gradient, isCurrency, startCounting }: {
    label: string; value: number; icon: React.ElementType; gradient: string;
    isCurrency?: boolean; startCounting: boolean;
}) {
    const animatedValue = useCountUp(value, 1200, startCounting);
    const displayValue = isCurrency
        ? `₦${animatedValue.toLocaleString()}`
        : animatedValue.toLocaleString();

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -3 }}
            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:shadow-lg transition-all"
        >
            <div className="flex items-start justify-between">
                <div className="space-y-1">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
                    <p className="text-2xl font-black text-slate-900 mt-1">{displayValue}</p>
                </div>
                <div className={`p-3 rounded-xl bg-gradient-to-br ${gradient} shadow-lg`}>
                    <Icon className="w-5 h-5 text-white" />
                </div>
            </div>
        </motion.div>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function ProductionFloorDetailPage() {
    const { floorId } = useParams<{ floorId: string }>();
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [activeSection, setActiveSection] = useState('overview');
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [showWastageModal, setShowWastageModal] = useState(false);
    const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
    const [expandedInventoryItem, setExpandedInventoryItem] = useState<string | null>(null);
    const [sectionInView, setSectionInView] = useState<Record<string, boolean>>({});

    // Inventory state
    const [inventorySearch, setInventorySearch] = useState('');
    const [inventoryFilter, setInventoryFilter] = useState<'all' | 'low' | 'out'>('all');
    const [inventoryPage, setInventoryPage] = useState(1);

    // Transfers state
    const [transferSearch, setTransferSearch] = useState('');
    const [transferStatusFilter, setTransferStatusFilter] = useState<string>('all');
    const [transferPage, setTransferPage] = useState(1);

    // Wastage state
    const [wastageSearch, setWastageSearch] = useState('');
    const [wastageTypeFilter, setWastageTypeFilter] = useState<string>('all');
    const [wastagePage, setWastagePage] = useState(1);

    const overviewRef = useRef<HTMLDivElement>(null);
    const inventoryRef = useRef<HTMLDivElement>(null);
    const transfersRef = useRef<HTMLDivElement>(null);
    const wastageRef = useRef<HTMLDivElement>(null);

    // ============================================================
    // QUERIES
    // ============================================================

    const { data: floor, isLoading } = useQuery({
        queryKey: ['production-floor', floorId],
        queryFn: () => getProductionFloor(user!.tenantId, floorId!),
        enabled: !!floorId,
    });

    const { data: dashboard, refetch: refetchDashboard } = useQuery({
        queryKey: ['floor-dashboard', floorId],
        queryFn: () => getFloorDashboard(user!.tenantId, floorId!),
        enabled: !!floorId,
        refetchInterval: 60000,
    });

    const { data: inventory, isLoading: inventoryLoading } = useQuery({
        queryKey: ['floor-inventory', floorId],
        queryFn: () => getFloorInventory(user!.tenantId, floorId!),
        enabled: !!floorId,
        refetchInterval: 30000,
    });

    const { data: transfers, isLoading: transfersLoading } = useQuery({
        queryKey: ['floor-transfers', floorId],
        queryFn: () => getFloorTransfers(user!.tenantId, { limit: 200, floorId }),
        enabled: !!floorId,
        refetchInterval: 30000,
    });

    const { data: wastage, isLoading: wastageLoading } = useQuery({
        queryKey: ['floor-wastage', floorId],
        queryFn: () => getFloorWastage(user!.tenantId, floorId!, { limit: 200 }),
        enabled: !!floorId,
        refetchInterval: 30000,
    });

    // ============================================================
    // FILTERED DATA
    // ============================================================

    const filteredInventory = useMemo(() => {
        let result = inventory || [];
        if (inventorySearch) {
            const q = inventorySearch.toLowerCase();
            result = result.filter(item =>
                (item.productName || '').toLowerCase().includes(q) ||
                (item.productSku || '').toLowerCase().includes(q)
            );
        }
        if (inventoryFilter === 'low') {
            result = result.filter(item => { const qty = parseFloat(item.quantityOnHand); return qty > 0 && qty <= 10; });
        } else if (inventoryFilter === 'out') {
            result = result.filter(item => parseFloat(item.quantityOnHand) <= 0);
        }
        return result;
    }, [inventory, inventorySearch, inventoryFilter]);

    const paginatedInventory = useMemo(() => {
        const start = (inventoryPage - 1) * ITEMS_PER_PAGE;
        return filteredInventory.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredInventory, inventoryPage]);

    const filteredTransfers = useMemo(() => {
        let result = transfers || [];
        if (transferSearch) {
            const q = transferSearch.toLowerCase();
            result = result.filter(t =>
                (t.sourceName || '').toLowerCase().includes(q) ||
                (t.destinationName || '').toLowerCase().includes(q) ||
                (t.notes || '').toLowerCase().includes(q)
            );
        }
        if (transferStatusFilter !== 'all') {
            result = result.filter(t => t.status === transferStatusFilter);
        }
        return result;
    }, [transfers, transferSearch, transferStatusFilter]);

    const paginatedTransfers = useMemo(() => {
        const start = (transferPage - 1) * ITEMS_PER_PAGE;
        return filteredTransfers.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredTransfers, transferPage]);

    const filteredWastage = useMemo(() => {
        let result = wastage || [];
        if (wastageSearch) {
            const q = wastageSearch.toLowerCase();
            result = result.filter(w =>
                (w.productName || '').toLowerCase().includes(q) ||
                (w.reason || '').toLowerCase().includes(q)
            );
        }
        if (wastageTypeFilter !== 'all') {
            result = result.filter(w => w.wastageType === wastageTypeFilter);
        }
        return result;
    }, [wastage, wastageSearch, wastageTypeFilter]);

    const paginatedWastage = useMemo(() => {
        const start = (wastagePage - 1) * ITEMS_PER_PAGE;
        return filteredWastage.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredWastage, wastagePage]);

    // ============================================================
    // SCROLL SPY
    // ============================================================

    useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.scrollY + 200;
            const sections = [
                { id: 'overview', ref: overviewRef },
                { id: 'inventory', ref: inventoryRef },
                { id: 'transfers', ref: transfersRef },
                { id: 'wastage', ref: wastageRef },
            ];
            for (let i = sections.length - 1; i >= 0; i--) {
                const section = sections[i];
                const element = section.ref.current;
                if (element && scrollY >= element.offsetTop) {
                    setActiveSection(section.id);
                    break;
                }
            }
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => { entries.forEach(e => { if (e.isIntersecting) setSectionInView(prev => ({ ...prev, [e.target.id]: true })); }); },
            { threshold: 0.3 }
        );
        [overviewRef, inventoryRef, transfersRef, wastageRef].forEach(ref => { if (ref.current) observer.observe(ref.current); });
        return () => observer.disconnect();
    }, []);

    const scrollToSection = (sectionId: string) => {
        const refs: Record<string, React.RefObject<HTMLDivElement | null>> = {
            overview: overviewRef, inventory: inventoryRef, transfers: transfersRef, wastage: wastageRef,
        };
        refs[sectionId]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setActiveSection(sectionId);
    };

    const handleRefresh = () => {
        refetchDashboard();
        queryClient.invalidateQueries({ queryKey: ['floor-inventory', floorId] });
        queryClient.invalidateQueries({ queryKey: ['floor-transfers', floorId] });
        queryClient.invalidateQueries({ queryKey: ['floor-wastage', floorId] });
    };

    const handleDeactivate = async () => {
        try {
            await deactivateProductionFloor(user!.tenantId, floor!.id);
            queryClient.invalidateQueries({ queryKey: ['production-floors'] });
            addToast({ type: 'success', title: 'Floor deactivated' });
            setShowDeactivateConfirm(false);
            navigate('/bom/production-floors');
        } catch (err: any) {
            addToast({ type: 'error', title: 'Failed', message: err?.message });
        }
    };

    // ============================================================
    // LOADING / ERROR STATES
    // ============================================================

    if (isLoading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="text-center">
                    <Spinner size="xl" />
                    <p className="text-sm text-slate-400 mt-4">Loading floor details...</p>
                </div>
            </div>
        );
    }

    if (!floor) {
        return (
            <ErrorState
                title="Production Floor Not Found"
                message="This floor may have been deleted or you don't have access."
                onRetry={() => navigate('/bom/production-floors')}
            />
        );
    }

    const Icon = FLOOR_ICONS[floor.floorType] || Warehouse;
    const gradient = FLOOR_COLORS[floor.floorType] || FLOOR_COLORS.general;
    const totalInventoryPages = Math.ceil(filteredInventory.length / ITEMS_PER_PAGE);
    const totalTransferPages = Math.ceil(filteredTransfers.length / ITEMS_PER_PAGE);
    const totalWastagePages = Math.ceil(filteredWastage.length / ITEMS_PER_PAGE);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50/30">
            {/* ================================================================ */}
            {/* HERO HEADER */}
            {/* ================================================================ */}
            <div className="relative overflow-hidden">
                <div className={`absolute inset-0 bg-gradient-to-r ${gradient} opacity-90`} />
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE4YzAtMS4xLS45LTItMi0yaC0xMmMtMS4xIDAtMiAuOS0yIDJ2MjRoMTR2LTI0eiIvPjwvZz48L2c+PC9zdmc+')] opacity-30" />

                <div className="relative max-w-[1600px] mx-auto px-6 lg:px-8 py-8">
                    {/* Back + Actions */}
                    <div className="flex items-center justify-between mb-6">
                        <button
                            onClick={() => navigate('/bom/production-floors')}
                            className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-xl text-white hover:bg-white/30 transition-colors text-sm font-medium"
                            type="button"
                        >
                            <ArrowLeft size={16} /> Back to Floors
                        </button>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleRefresh}
                                className="p-2.5 bg-white/20 backdrop-blur-sm rounded-xl text-white hover:bg-white/30 transition-colors"
                                type="button"
                                title="Refresh data"
                            >
                                <RefreshCw size={18} />
                            </button>
                            <button
                                onClick={() => navigate(`/bom/production-floors/${floorId}/edit`)}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white/20 backdrop-blur-sm rounded-xl text-white hover:bg-white/30 transition-colors text-sm font-medium"
                                type="button"
                            >
                                <Pencil size={16} /> Edit
                            </button>
                            {floor.isActive && (
                                <button
                                    onClick={() => setShowDeactivateConfirm(true)}
                                    className="flex items-center gap-2 px-4 py-2.5 bg-white/20 backdrop-blur-sm rounded-xl text-white hover:bg-rose-500/50 transition-colors text-sm font-medium"
                                    type="button"
                                >
                                    <Trash2 size={16} /> Deactivate
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Floor Identity */}
                    <div className="flex items-start gap-6">
                        <div className="p-4 bg-white/20 backdrop-blur-sm rounded-3xl shadow-2xl">
                            <Icon size={48} className="text-white" />
                        </div>
                        <div className="text-white flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                                <h1 className="text-3xl font-black">{floor.name}</h1>
                                <Badge variant={floor.isActive ? 'success' : 'danger'} size="md" className="backdrop-blur-sm">
                                    {floor.status}
                                </Badge>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-white/80 flex-wrap">
                                <span className="flex items-center gap-1"><MapPin size={14} />{floor.branchName || 'Unknown Branch'}</span>
                                {floor.manager && <span className="flex items-center gap-1"><User size={14} />{floor.manager}</span>}
                                {floor.contactPhone && <span className="flex items-center gap-1"><Phone size={14} />{floor.contactPhone}</span>}
                                {floor.contactEmail && <span className="flex items-center gap-1"><Mail size={14} />{floor.contactEmail}</span>}
                            </div>
                            {floor.description && (
                                <p className="text-sm text-white/70 mt-2 max-w-2xl">{floor.description}</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ================================================================ */}
            {/* STICKY NAVIGATION */}
            {/* ================================================================ */}
            <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8">
                    <div className="flex items-center gap-1 py-2 overflow-x-auto">
                        {NAV_ITEMS.map(item => (
                            <button
                                key={item.id}
                                onClick={() => scrollToSection(item.id)}
                                className={clsx(
                                    'flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap',
                                    activeSection === item.id
                                        ? 'bg-amber-100 text-amber-700 shadow-sm'
                                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                                )}
                                type="button"
                            >
                                <item.icon size={16} />
                                {item.label}
                                {item.id === 'inventory' && (
                                    <span className="text-[10px] bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded-full">
                                        {filteredInventory.length}
                                    </span>
                                )}
                                {item.id === 'transfers' && (
                                    <span className="text-[10px] bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded-full">
                                        {filteredTransfers.length}
                                    </span>
                                )}
                                {item.id === 'wastage' && (
                                    <span className="text-[10px] bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded-full">
                                        {filteredWastage.length}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* ================================================================ */}
            {/* CONTENT SECTIONS */}
            {/* ================================================================ */}
            <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-8 space-y-8">
                {/* ============================================================ */}
                {/* OVERVIEW SECTION */}
                {/* ============================================================ */}
                <div ref={overviewRef} id="overview">
                    <div className="flex items-center gap-3 mb-4">
                        <BarChart3 size={20} className="text-amber-600" />
                        <h2 className="text-lg font-bold text-slate-800">Overview</h2>
                    </div>

                    {/* Quick Action Buttons */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                        <QuickActionButton icon={Truck} label="Transfer Stock" sublabel="Send to floor" color="amber" onClick={() => setShowTransferModal(true)} />
                        <QuickActionButton icon={AlertTriangle} label="Record Wastage" sublabel="Log waste" color="rose" onClick={() => setShowWastageModal(true)} />
                        <QuickActionButton icon={Package} label="View Inventory" sublabel={`${filteredInventory.length} items`} color="slate" onClick={() => scrollToSection('inventory')} />
                        <QuickActionButton icon={Zap} label="Production" sublabel="Start batch" color="emerald" onClick={() => navigate('/bom/production')} />
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <AnimatedStatCard label="Inventory Items" value={dashboard?.inventory.totalItems || 0} icon={Package} gradient="from-amber-500 to-orange-600" startCounting={sectionInView['overview'] || false} />
                        <AnimatedStatCard label="Active Batches" value={dashboard?.production.activeBatches || 0} icon={Zap} gradient="from-emerald-500 to-teal-600" startCounting={sectionInView['overview'] || false} />
                        <AnimatedStatCard label="Today's Wastage" value={dashboard?.wastage.todayWastageCount || 0} icon={AlertTriangle} gradient="from-rose-500 to-rose-600" startCounting={sectionInView['overview'] || false} />
                        <AnimatedStatCard label="Wastage Value" value={dashboard?.wastage.todayWastageValue || 0} icon={DollarSign} gradient="from-slate-500 to-slate-700" isCurrency startCounting={sectionInView['overview'] || false} />
                    </div>

                    {/* Recent Activity Timeline */}
                    <div className="bg-white rounded-2xl border border-slate-200 p-6">
                        <h3 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
                            <Activity size={16} className="text-amber-500" />
                            Recent Activity
                        </h3>
                        <div className="space-y-4">
                            {transfers?.slice(0, 3).map((t) => (
                                <div key={t.id} className="flex gap-4 items-start">
                                    <div className="w-2 h-2 mt-2 rounded-full bg-emerald-500 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-slate-700 truncate">
                                            Transfer: {t.sourceName} → {t.destinationName}
                                        </p>
                                        <p className="text-xs text-slate-400">
                                            {t.totalItems} items · {t.totalQuantity.toLocaleString()} total qty · {new Date(t.createdAt).toLocaleDateString('en-NG')}
                                        </p>
                                    </div>
                                    <Badge variant={t.status === 'received' ? 'success' : 'warning'} size="sm">{t.status}</Badge>
                                </div>
                            ))}
                            {wastage?.slice(0, 3).map((w) => (
                                <div key={w.id} className="flex gap-4 items-start">
                                    <div className="w-2 h-2 mt-2 rounded-full bg-rose-500 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-slate-700 truncate">
                                            Wastage: {w.productName} — {w.quantity} {w.uomName}
                                        </p>
                                        <p className="text-xs text-slate-400">
                                            {w.reason} · {new Date(w.createdAt).toLocaleDateString('en-NG')}
                                        </p>
                                    </div>
                                    <Badge variant="danger" size="sm">{w.wastageType.replace(/_/g, ' ')}</Badge>
                                </div>
                            ))}
                            {(!transfers?.length && !wastage?.length) && (
                                <p className="text-sm text-slate-400 text-center py-4">No recent activity on this floor</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* ============================================================ */}
                {/* INVENTORY SECTION */}
                {/* ============================================================ */}
                <div ref={inventoryRef} id="inventory">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <Package size={20} className="text-amber-600" />
                            <h2 className="text-lg font-bold text-slate-800">Floor Inventory</h2>
                            <Badge variant="neutral" size="sm">{filteredInventory.length} items</Badge>
                        </div>
                        <button
                            onClick={() => setShowTransferModal(true)}
                            className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-white rounded-xl text-sm font-semibold hover:bg-amber-600 transition-colors shadow-sm"
                            type="button"
                        >
                            <Plus size={16} /> Transfer In
                        </button>
                    </div>

                    {/* Search & Filters */}
                    <div className="bg-white rounded-2xl border border-slate-200 p-3 mb-3">
                        <div className="flex flex-col sm:flex-row gap-2">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input type="text" placeholder="Search product name or SKU..." value={inventorySearch}
                                    onChange={(e) => { setInventorySearch(e.target.value); setInventoryPage(1); }}
                                    className="w-full pl-10 pr-10 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 focus:bg-white transition-all" />
                                {inventorySearch && (
                                    <button onClick={() => { setInventorySearch(''); setInventoryPage(1); }} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600" type="button"><X className="w-4 h-4" /></button>
                                )}
                            </div>
                            <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
                                {[{ key: 'all', label: 'All' }, { key: 'low', label: 'Low Stock' }, { key: 'out', label: 'Out of Stock' }].map(f => (
                                    <button key={f.key} onClick={() => { setInventoryFilter(f.key as any); setInventoryPage(1); }}
                                        className={clsx('px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap',
                                            inventoryFilter === f.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700')} type="button">{f.label}</button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                        {inventoryLoading ? <TableSkeleton rows={8} /> : !inventory || inventory.length === 0 ? (
                            <div className="text-center py-16">
                                <Package size={48} className="text-slate-300 mx-auto mb-3" />
                                <p className="text-slate-500 font-medium">No inventory on this floor</p>
                                <p className="text-sm text-slate-400 mt-1">Transfer stock to this floor to get started</p>
                                <button onClick={() => setShowTransferModal(true)} className="mt-4 px-5 py-2.5 bg-amber-500 text-white rounded-xl text-sm font-semibold hover:bg-amber-600 transition-colors" type="button">Transfer Stock</button>
                            </div>
                        ) : filteredInventory.length === 0 ? (
                            <div className="text-center py-16">
                                <Search size={48} className="text-slate-300 mx-auto mb-3" />
                                <p className="text-slate-500 font-medium">No matching items</p>
                                <button onClick={() => { setInventorySearch(''); setInventoryFilter('all'); }} className="mt-4 text-amber-600 font-medium text-sm" type="button">Clear filters</button>
                            </div>
                        ) : (
                            <>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-slate-100">
                                        <thead>
                                            <tr className="bg-slate-50/80">
                                                <th className="px-6 py-3.5 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Product</th>
                                                <th className="px-6 py-3.5 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">Qty On Hand</th>
                                                <th className="px-6 py-3.5 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">UOM</th>
                                                <th className="px-6 py-3.5 text-left text-xs font-bold text-slate-500 uppercase tracking-wider hidden md:table-cell">Last Updated</th>
                                                <th className="px-6 py-3.5 w-10"></th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {paginatedInventory.map((item) => (
                                                <React.Fragment key={item.id}>
                                                    <tr onClick={() => setExpandedInventoryItem(expandedInventoryItem === item.id ? null : item.id)}
                                                        className="hover:bg-amber-50/30 cursor-pointer transition-colors">
                                                        <td className="px-6 py-3.5">
                                                            <p className="text-sm font-semibold text-slate-800">{item.productName || 'Unknown Product'}</p>
                                                            {item.productSku && <p className="text-xs text-slate-400 font-mono mt-0.5">{item.productSku}</p>}
                                                        </td>
                                                        <td className="px-6 py-3.5 text-right">
                                                            <span className={clsx('text-sm font-bold tabular-nums',
                                                                parseFloat(item.quantityOnHand) <= 0 ? 'text-rose-600 bg-rose-50 px-2 py-1 rounded-lg' :
                                                                    parseFloat(item.quantityOnHand) <= 10 ? 'text-amber-600 bg-amber-50 px-2 py-1 rounded-lg' :
                                                                        'text-slate-700')}>
                                                                {parseFloat(item.quantityOnHand).toLocaleString()}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-3.5 text-sm text-slate-500">{item.uomAbbreviation || item.uomName}</td>
                                                        <td className="px-6 py-3.5 text-xs text-slate-400 hidden md:table-cell">{new Date(item.lastUpdated).toLocaleDateString('en-NG')}</td>
                                                        <td className="px-6 py-3.5">
                                                            {expandedInventoryItem === item.id ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                                                        </td>
                                                    </tr>
                                                    <AnimatePresence>
                                                        {expandedInventoryItem === item.id && (
                                                            <motion.tr initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="bg-amber-50/20">
                                                                <td colSpan={5} className="px-6 py-4">
                                                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                                                        <div><p className="text-xs text-slate-400 font-semibold uppercase">Product ID</p><p className="font-mono text-xs text-slate-600 mt-0.5">{item.productId}</p></div>
                                                                        <div><p className="text-xs text-slate-400 font-semibold uppercase">UOM ID</p><p className="font-mono text-xs text-slate-600 mt-0.5">{item.uomId}</p></div>
                                                                        <div><p className="text-xs text-slate-400 font-semibold uppercase">Floor ID</p><p className="font-mono text-xs text-slate-600 mt-0.5">{item.floorId}</p></div>
                                                                        <div><p className="text-xs text-slate-400 font-semibold uppercase">Record ID</p><p className="font-mono text-xs text-slate-600 mt-0.5">{item.id}</p></div>
                                                                    </div>
                                                                </td>
                                                            </motion.tr>
                                                        )}
                                                    </AnimatePresence>
                                                </React.Fragment>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <Pagination page={inventoryPage} totalPages={totalInventoryPages} totalItems={filteredInventory.length} onPageChange={setInventoryPage} />
                            </>
                        )}
                    </div>
                </div>

                {/* ============================================================ */}
                {/* TRANSFERS SECTION */}
                {/* ============================================================ */}
                <div ref={transfersRef} id="transfers">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <Truck size={20} className="text-amber-600" />
                            <h2 className="text-lg font-bold text-slate-800">Stock Transfers</h2>
                            <Badge variant="neutral" size="sm">{filteredTransfers.length}</Badge>
                        </div>
                        <button onClick={() => setShowTransferModal(true)}
                            className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-white rounded-xl text-sm font-semibold hover:bg-amber-600 transition-colors shadow-sm" type="button">
                            <Plus size={16} /> New Transfer
                        </button>
                    </div>

                    {/* Search & Filters */}
                    <div className="bg-white rounded-2xl border border-slate-200 p-3 mb-3">
                        <div className="flex flex-col sm:flex-row gap-2">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input type="text" placeholder="Search transfers..." value={transferSearch}
                                    onChange={(e) => { setTransferSearch(e.target.value); setTransferPage(1); }}
                                    className="w-full pl-10 pr-10 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400" />
                                {transferSearch && <button onClick={() => { setTransferSearch(''); setTransferPage(1); }} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400" type="button"><X className="w-4 h-4" /></button>}
                            </div>
                            <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
                                {['all', 'pending', 'approved', 'received', 'cancelled'].map(s => (
                                    <button key={s} onClick={() => { setTransferStatusFilter(s); setTransferPage(1); }}
                                        className={clsx('px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap',
                                            transferStatusFilter === s ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700')} type="button">{s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}</button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                        {transfersLoading ? <TableSkeleton rows={5} /> : !transfers || transfers.length === 0 ? (
                            <div className="text-center py-12"><Truck size={40} className="text-slate-300 mx-auto mb-3" /><p className="text-slate-500 font-medium">No transfers yet</p></div>
                        ) : filteredTransfers.length === 0 ? (
                            <div className="text-center py-12"><Search size={40} className="text-slate-300 mx-auto mb-3" /><p className="text-slate-500 font-medium">No matching transfers</p></div>
                        ) : (
                            <>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-slate-100">
                                        <thead><tr className="bg-slate-50/80">
                                            <th className="px-6 py-3.5 text-left text-xs font-bold text-slate-500 uppercase">From → To</th>
                                            <th className="px-6 py-3.5 text-center text-xs font-bold text-slate-500 uppercase">Items</th>
                                            <th className="px-6 py-3.5 text-right text-xs font-bold text-slate-500 uppercase">Total Qty</th>
                                            <th className="px-6 py-3.5 text-center text-xs font-bold text-slate-500 uppercase">Status</th>
                                            <th className="px-6 py-3.5 text-left text-xs font-bold text-slate-500 uppercase hidden md:table-cell">Date</th>
                                        </tr></thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {paginatedTransfers.map((t) => (
                                                <tr key={t.id} className="hover:bg-slate-50 transition-colors">
                                                    <td className="px-6 py-3.5">
                                                        <p className="text-sm text-slate-700"><span className="font-medium">{t.sourceName || '—'}</span> → <span className="font-medium">{t.destinationName || '—'}</span></p>
                                                    </td>
                                                    <td className="px-6 py-3.5 text-sm text-center text-slate-600">{t.totalItems}</td>
                                                    <td className="px-6 py-3.5 text-sm text-right font-semibold text-slate-700 tabular-nums">{t.totalQuantity.toLocaleString()}</td>
                                                    <td className="px-6 py-3.5 text-center">
                                                        <Badge variant={t.status === 'received' ? 'success' : t.status === 'pending' ? 'warning' : t.status === 'cancelled' ? 'danger' : 'info'} size="sm">{t.status}</Badge>
                                                    </td>
                                                    <td className="px-6 py-3.5 text-xs text-slate-400 hidden md:table-cell">{new Date(t.createdAt).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <Pagination page={transferPage} totalPages={totalTransferPages} totalItems={filteredTransfers.length} onPageChange={setTransferPage} />
                            </>
                        )}
                    </div>
                </div>

                {/* ============================================================ */}
                {/* WASTAGE SECTION */}
                {/* ============================================================ */}
                <div ref={wastageRef} id="wastage">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <AlertTriangle size={20} className="text-amber-600" />
                            <h2 className="text-lg font-bold text-slate-800">Wastage Log</h2>
                            <Badge variant="neutral" size="sm">{filteredWastage.length}</Badge>
                        </div>
                        <button onClick={() => setShowWastageModal(true)}
                            className="flex items-center gap-2 px-4 py-2.5 bg-rose-500 text-white rounded-xl text-sm font-semibold hover:bg-rose-600 transition-colors shadow-sm" type="button">
                            <Plus size={16} /> Record Wastage
                        </button>
                    </div>

                    {/* Search & Filters */}
                    <div className="bg-white rounded-2xl border border-slate-200 p-3 mb-3">
                        <div className="flex flex-col sm:flex-row gap-2">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input type="text" placeholder="Search by product or reason..." value={wastageSearch}
                                    onChange={(e) => { setWastageSearch(e.target.value); setWastagePage(1); }}
                                    className="w-full pl-10 pr-10 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-rose-500/20 focus:border-rose-400" />
                                {wastageSearch && <button onClick={() => { setWastageSearch(''); setWastagePage(1); }} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400" type="button"><X className="w-4 h-4" /></button>}
                            </div>
                            <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
                                {['all', 'raw_material', 'production_loss', 'spoilage', 'unsold', 'overproduction'].map(t => (
                                    <button key={t} onClick={() => { setWastageTypeFilter(t); setWastagePage(1); }}
                                        className={clsx('px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap',
                                            wastageTypeFilter === t ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700')} type="button">{t === 'all' ? 'All' : t.replace(/_/g, ' ')}</button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                        {wastageLoading ? <TableSkeleton rows={5} /> : !wastage || wastage.length === 0 ? (
                            <div className="text-center py-12"><AlertTriangle size={40} className="text-slate-300 mx-auto mb-3" /><p className="text-slate-500 font-medium">No wastage recorded</p></div>
                        ) : filteredWastage.length === 0 ? (
                            <div className="text-center py-12"><Search size={40} className="text-slate-300 mx-auto mb-3" /><p className="text-slate-500 font-medium">No matching records</p></div>
                        ) : (
                            <>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-slate-100">
                                        <thead><tr className="bg-slate-50/80">
                                            <th className="px-6 py-3.5 text-left text-xs font-bold text-slate-500 uppercase">Product</th>
                                            <th className="px-6 py-3.5 text-right text-xs font-bold text-slate-500 uppercase">Qty</th>
                                            <th className="px-6 py-3.5 text-center text-xs font-bold text-slate-500 uppercase">Type</th>
                                            <th className="px-6 py-3.5 text-right text-xs font-bold text-slate-500 uppercase">Cost</th>
                                            <th className="px-6 py-3.5 text-left text-xs font-bold text-slate-500 uppercase">Reason</th>
                                            <th className="px-6 py-3.5 text-left text-xs font-bold text-slate-500 uppercase hidden md:table-cell">Date</th>
                                        </tr></thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {paginatedWastage.map((w) => (
                                                <tr key={w.id} className="hover:bg-slate-50 transition-colors">
                                                    <td className="px-6 py-3.5 text-sm font-medium text-slate-800">{w.productName || 'Unknown'}</td>
                                                    <td className="px-6 py-3.5 text-sm text-right font-semibold text-rose-600 tabular-nums">{parseFloat(w.quantity).toLocaleString()} <span className="text-xs text-slate-400">{w.uomName}</span></td>
                                                    <td className="px-6 py-3.5 text-center"><Badge variant="danger" size="sm">{w.wastageType.replace(/_/g, ' ')}</Badge></td>
                                                    <td className="px-6 py-3.5 text-sm text-right text-slate-600 tabular-nums">₦{parseFloat(w.costValue).toLocaleString()}</td>
                                                    <td className="px-6 py-3.5 text-sm text-slate-500 max-w-[200px] truncate">{w.reason}</td>
                                                    <td className="px-6 py-3.5 text-xs text-slate-400 hidden md:table-cell">{new Date(w.createdAt).toLocaleDateString('en-NG', { day: 'numeric', month: 'short' })}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <Pagination page={wastagePage} totalPages={totalWastagePages} totalItems={filteredWastage.length} onPageChange={setWastagePage} />
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* ================================================================ */}
            {/* MODALS */}
            {/* ================================================================ */}
            {showTransferModal && floor && (
                <TransferToFloorModal isOpen={showTransferModal} onClose={() => setShowTransferModal(false)} floor={floor} />
            )}
            {showWastageModal && floor && (
                <RecordWastageModal isOpen={showWastageModal} onClose={() => setShowWastageModal(false)} floor={floor} />
            )}
            <AnimatePresence>
                {showDeactivateConfirm && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowDeactivateConfirm(false)}>
                        <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                            className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6 text-center" onClick={e => e.stopPropagation()}>
                            <div className="w-16 h-16 rounded-2xl bg-rose-100 flex items-center justify-center mx-auto mb-4"><AlertTriangle size={32} className="text-rose-500" /></div>
                            <h3 className="text-lg font-bold text-slate-900">Deactivate Floor?</h3>
                            <p className="text-sm text-slate-500 mt-2"><strong>{floor.name}</strong> will no longer be available for production.</p>
                            <div className="flex gap-3 mt-6">
                                <button onClick={() => setShowDeactivateConfirm(false)} className="flex-1 px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm font-medium hover:bg-slate-50" type="button">Cancel</button>
                                <button onClick={handleDeactivate} className="flex-1 px-4 py-2.5 bg-rose-500 text-white rounded-xl text-sm font-semibold hover:bg-rose-600" type="button">Deactivate</button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

// ============================================================
// QUICK ACTION BUTTON
// ============================================================

function QuickActionButton({ icon: Icon, label, sublabel, color, onClick }: {
    icon: React.ElementType; label: string; sublabel: string;
    color: 'amber' | 'rose' | 'slate' | 'emerald'; onClick: () => void;
}) {
    const colors = {
        amber: { border: 'border-amber-300 hover:border-amber-400', bg: 'hover:bg-amber-50', iconBg: 'bg-amber-100', iconColor: 'text-amber-600' },
        rose: { border: 'border-rose-300 hover:border-rose-400', bg: 'hover:bg-rose-50', iconBg: 'bg-rose-100', iconColor: 'text-rose-600' },
        slate: { border: 'border-slate-200 hover:border-slate-300', bg: 'hover:bg-slate-50', iconBg: 'bg-slate-100', iconColor: 'text-slate-600' },
        emerald: { border: 'border-emerald-300 hover:border-emerald-400', bg: 'hover:bg-emerald-50', iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600' },
    };
    const c = colors[color];
    return (
        <button onClick={onClick}
            className={clsx('flex items-center gap-3 p-4 bg-white rounded-2xl border-2 border-dashed transition-all text-left', c.border, c.bg)} type="button">
            <div className={clsx('p-2.5 rounded-xl', c.iconBg)}><Icon size={20} className={c.iconColor} /></div>
            <div><p className="text-sm font-bold text-slate-700">{label}</p><p className="text-xs text-slate-400">{sublabel}</p></div>
        </button>
    );
}